// File: src/pages/seller/AddProduct/validation/productValidationSchema.js
import * as yup from "yup";

const productValidationSchema = ({ kycType, categories = [] } = {}) => {
  return yup.object().shape({
    // Basic Info
    category_id: yup.string().required("Category is required"),
    subcategory_id: yup.string().required("Subcategory is required"),
    name: yup.string().trim().required("Product name is required"),
    description: yup
      .string()
      .test("not-empty", "Description is required", (val) => {
        if (!val) return false;
        const plain = val.replace(/<[^>]*>/g, "").replace(/\s+/g, "");
        return Boolean(plain);
      }),
    location: yup.string().trim().required("Location is required"),

    // Pricing & Quantity
    // price: yup
    //   .number()
    //   .typeError("Price must be a number")
    //   .positive("Price must be greater than 0")
    //   .required("Price is required"),
    quantity: yup
      .number()
      .transform((val, orig) => (orig === "" ? undefined : val))
      .typeError("Quantity must be a number")
      .positive("Quantity must be greater than 0")
      .required("Quantity is required"),
    unit_of_measurement: yup.string().required("Unit is required"),
    calc_unit: yup.string().required("Unit is required"),
    currency: yup.string().required("Currency is required"),

    //  Shipping / Trade Terms
    incoterms: yup.string().required("Incoterms is required"),
    payment_terms: yup.string().required("Payment terms is required"),
    lead_time: yup
      .number()
      .transform((val, orig) => (orig === "" ? undefined : val))
      .typeError("Lead time must be a number")
      .min(1, "Lead time must be at least 1 day")
      .required("Lead time is required"),
    delivery_port: yup.string().nullable(),

    //  Technical & Metal Fields
    metal_code: yup.string().nullable(),
    sorting_type: yup
      .string()
      .trim()
      .test("required", "Sorting type is required", (val) => Boolean(val && val.length > 0))
      .required("Sorting type is required"),
    attachment: yup
      .string()
      .trim()
      .test("required", "Attachment is required", (val) => Boolean(val && val.length > 0))
      .required("Attachment is required"),
    waste_percentage: yup.string().nullable(),
    waste_range: yup
      .string()
      .trim()
      .test("required", "Waste range is required", (val) => Boolean(val && val.length > 0))
      .required("Waste range is required"),
    toxic_level: yup.string().nullable(),
    hazard_types: yup
      .array()
      .of(yup.string())
      .test("required", "Hazard selection is required", (val) => Boolean(val && val.length > 0))
      .min(1, "Hazard selection is required")
      .required("Hazard selection is required"),

    //  Pricing & Weight
    price_per_unit: yup.string().nullable(),
    weight: yup.string().nullable(),
    calc_weight: yup
      .number()
      .transform((val, orig) => (orig === "" ? undefined : val))
      .test(
        "required-if-non-ferrous",
        "Weight is required",
        function (value) {
          const categoryId = this.parent?.category_id;
          if (!categoryId || !categories || !Array.isArray(categories))
            return true;
          const cat = categories.find(
            (c) => String(c.id) === String(categoryId)
          );
          const name = (cat?.name || "").toLowerCase();
          // Require weight whenever Price Estimation section is visible:
          // that is Non-Ferrous OR Precious Metal
          const needsWeight =
            name.includes("non-ferrous") ||
            name.includes("non ferrous") ||
            name.includes("precious");
          if (!needsWeight) return true;
          if (value === undefined || value === null || isNaN(value))
            return this.createError({ message: "Weight is required" });
          if (value <= 0)
            return this.createError({
              message: "Weight must be greater than 0",
            });
          return true;
        }
      ),
    estimated_price: yup.string().nullable(),

    // Specifications
    specifications: yup.array().of(
      yup.object().shape({
        value: yup.string().test("required-with-label", function (value) {
          const { label } = this.parent || {};
          const fieldName = label || "Specification";
          if (!value || !value.trim()) {
            return this.createError({ message: `${fieldName} is required` });
          }
          return true;
        }),
        label: yup.string(),
        input_type: yup.string(),
        field_id: yup.number(),
      })
    ),

    // FAQs
    faqs: yup.array().of(
      yup.object().shape({
        question: yup.string().trim().nullable(),
        answer: yup.string().trim().nullable(),
      })
    ),

    // Offer Pricing
    max_buying_price: yup
      .number()
      .typeError("Maximum buying price must be a number")
      .positive("Maximum buying price must be greater than 0")
      .nullable(),
    // discount_percentage: yup
    //   .number()
    //   .transform((value) => (value === "" || value === null ? null : value))
    //   .typeError("Discount must be a number")
    //   .min(0, "Discount cannot be less than 0")
    //   .max(100, "Discount cannot be more than 100")
    //   .nullable()
    //   .notRequired(),

    // Bidding Fields
    // is_reverse_auction: yup.boolean().nullable(),
    // minimum_asking_price: yup
    //   .number()
    //   .typeError("Minimum asking price must be a number")
    //   .when("allow_bidding", {
    //     is: 1,
    //     then: (schema) => schema.required("Minimum asking price is required"),
    //     otherwise: (schema) => schema.optional().nullable(true),
    //   })
    //   .nullable(),
    // bid_increment: yup
    //   .number()
    //   .typeError("Bid increment must be a number")
    //   .when("allow_bidding", {
    //     is: 1,
    //     then: (schema) => schema.required("Bid increment is required"),
    //     otherwise: (schema) => schema.optional().nullable(true),
    //   })
    //   .nullable(),
    // bid_closed_at: yup.string().nullable(),
    // reverse_close_price: yup
    //   .mixed()
    //   //  always normalize empty string -> undefined so .when() sees "no value"
    //   .transform((val) => (val === "" ? undefined : val))
    //   .when("is_reverse_auction", {
    //     is: true,
    //     then: (schema) =>
    //       yup
    //         .number()
    //         .transform((val, orig) => (orig === "" ? undefined : val))
    //         .typeError("Reverse close price must be a number")
    //         .positive("Reverse close price must be greater than 0")
    //         .required("Reverse close price is required for reverse auction"),
    //     otherwise: (schema) =>
    //       yup
    //         .number()
    //         .transform((val, orig) => (orig === "" ? undefined : val))
    //         .typeError("Reverse close price must be a number")
    //         .positive("Reverse close price must be greater than 0")
    //         .nullable(),
    //   }),
    // auction_status: yup
    //   .string()
    //   .oneOf(["open", "closed", "pending", "cancelled"])
    //   .nullable(),

    // Media
    image: yup.mixed().nullable(),
    video:
      kycType === "individual"
        ? yup.mixed().required("Video is required for individual KYC")
        : yup.mixed().nullable(),
  });
};

export default productValidationSchema;
